/**
 * @file: provider.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Cross-screen bus for the reservations flow. A screen that mutates a
 * reservation (the detail page's status transitions — notably cancel — and its
 * item add/remove) emits the updated row on `reservationUpdated$`; sibling
 * screens (the list) subscribe to patch their copy + recount in place. Like the
 * lead center, this is deliberately decoupled from navigation params — passing a
 * callback through `navigate(...)` breaks deep-linking and isn't serializable.
 */

import { Reservation } from '@euler/model/reservation/Reservation';
import { useCreation } from '@euler/utils/hooks';
import { ReactNode, createContext, memo, useContext, useMemo } from 'react';
import { Subject } from 'rxjs';

export type ReservationCenterContextValue = {
  // A reservation changed (items edited, rescheduled, confirmed/arrived,
  // converted); publishers `.next(row)` the full updated row, subscribers patch.
  reservationUpdated$: Subject<Reservation>;
  // A reservation was cancelled — a dedicated channel so consumers can react to
  // cancel specifically (the cancelled row is carried).
  reservationCanceled$: Subject<Reservation>;
  // A reservation was deleted; carries its id so consumers drop their copy.
  reservationDeleted$: Subject<string>;
};

const ReservationCenterContext = createContext<ReservationCenterContextValue>(
  null as any,
);

export const useReservationCenter = () => useContext(ReservationCenterContext);

export const ReservationCenterProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const reservationUpdated$ = useCreation(
      () => new Subject<Reservation>(),
      [],
    );
    const reservationCanceled$ = useCreation(
      () => new Subject<Reservation>(),
      [],
    );
    const reservationDeleted$ = useCreation(() => new Subject<string>(), []);
    const value = useMemo<ReservationCenterContextValue>(
      () => ({
        reservationUpdated$,
        reservationCanceled$,
        reservationDeleted$,
      }),
      [reservationUpdated$, reservationCanceled$, reservationDeleted$],
    );
    return (
      <ReservationCenterContext.Provider value={value}>
        {children}
      </ReservationCenterContext.Provider>
    );
  },
);
